import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import axios from 'axios';
import { Box, CircularProgress, Alert, Button } from '@mui/material';

// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Actual form component that uses Stripe hooks
const CheckoutForm = ({ onClose, onPaymentSuccess, parentName, playerName, parentEmail, parentPhone, sessionId }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements) return;

    setLoading(true);
    setError(null);

    const { error: submitError } = await elements.submit();
    if (submitError) {
      setError(submitError.message);
      setLoading(false);
      return;
    }

    const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: 'if_required'
    });

    if (confirmError) {
      setError(confirmError.message);
      setLoading(false);
    } else if (paymentIntent && paymentIntent.status === 'succeeded') {
      try {
        // Call payment-success endpoint
        await axios.post('https://api.narrowgatepath.com/api/v2/stripe/payment-success', {
          paymentIntentId: paymentIntent.id,
          sessionId: sessionId,
          customerData: {
            parentName,
            playerName,
            parentEmail,
            parentPhone
          }
        });
        onPaymentSuccess(paymentIntent);
        onClose();
      } catch (error) {
        console.error('Error processing payment success:', error);
        setError('Payment successful but booking failed. Please contact support.');
        setLoading(false);
      }
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement />
      {error && (
        <Box sx={{ mt: 2, mb: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={!stripe || loading}
          sx={{
            minWidth: '120px',
            '&:hover': {
              backgroundColor: 'primary.dark',
            }
          }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            'Pay Now'
          )}
        </Button>
      </Box>
    </form>
  );
};

// Wrapper component that handles the payment intent creation
const StripePaymentForm = ({ 
  onClose, 
  sessionId, 
  sessionPrice, 
  playerName,
  parentName,
  parentEmail,
  parentPhone,
  onPaymentSuccess 
}) => {
  const [clientSecret, setClientSecret] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    const createPaymentIntent = async () => {
      try {
        const { data } = await axios.post('https://api.narrowgatepath.com/api/v2/stripe/create-payment-intent', {
          sessionId,
          playerName,
          parentName,
          parentEmail,
          parentPhone
        });
        setClientSecret(data.clientSecret);
      } catch (error) {
        console.error('Error creating payment intent:', error);
        setError(error.response?.data?.error || 'Failed to initialize payment');
      }
    };

    createPaymentIntent();
  }, [sessionId, playerName, parentName, parentEmail, parentPhone]);

  return (
    <Box sx={{ mt: 2 }}>
      {!clientSecret ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Elements 
          stripe={stripePromise} 
          options={{
            clientSecret,
            appearance: {
              theme: 'stripe',
              variables: {
                colorPrimary: '#1976d2',
                colorBackground: '#ffffff',
                borderRadius: '4px'
              },
              rules: {
                '.Tab': {
                  border: '1px solid #e0e0e0',
                  boxShadow: '0px 1px 3px 0px rgba(0,0,0,0.1)'
                },
                '.Tab:hover': {
                  color: '#1976d2'
                },
                '.Tab--selected': {
                  borderColor: '#1976d2'
                },
                '.Label': {
                  fontWeight: '500'
                },
                '.Input': {
                  padding: '8px'
                }
              }
            }
          }}
        >
          <CheckoutForm 
            onClose={onClose} 
            onPaymentSuccess={onPaymentSuccess} 
            parentName={parentName}
            playerName={playerName}
            parentEmail={parentEmail}
            parentPhone={parentPhone}
            sessionId={sessionId}
          />
        </Elements>
      )}
      {error && (
        <Box sx={{ mt: 2, mb: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}
    </Box>
  );
};

export default StripePaymentForm;
